'use client';

import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Building2, Users, CalendarDays, FlaskConical,
  FileEdit, CreditCard, MailCheck, CheckCircle2
} from 'lucide-react';

export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      number: 1,
      icon: <Building2 className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Identificação',
      description: 'Informe o CNPJ ou CPF para iniciar.'
    },
    {
      number: 2,
      icon: <Users className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Colaboradores',
      description: 'Adicione a equipe de forma rápida.'
    },
    {
      number: 3,
      icon: <CalendarDays className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Local e Data',
      description: 'Escolha a clínica e horário ideais.'
    },
    {
      number: 4,
      icon: <FlaskConical className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Mapeamento',
      description: 'Defina os exames necessários.'
    },
    {
      number: 5,
      icon: <FileEdit className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Validação',
      description: 'Revise os dados e valores do pedido.'
    },
    {
      number: 6,
      icon: <CreditCard className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Pagamento',
      description: 'Liquidação segura via PIX ou Cartão.'
    },
    {
      number: 7,
      icon: <MailCheck className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Emissão',
      description: 'Guias e encaminhamentos enviados.'
    },
    {
      number: 8,
      icon: <CheckCircle2 className="w-8 h-8" strokeWidth={1.5} />,
      title: 'Conclusão',
      description: 'Atendimento garantido na clínica.'
    }
  ];

  // Auto-play mechanic
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500); // changes every 2.5s

    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <section id="comoFunciona" className="py-32 bg-gray-50 relative overflow-hidden">

      {/* Irregular Curved Line in the Background connecting steps */}
      <svg viewBox="0 0 1600 600" className="absolute w-full h-[600px] top-[150px] left-0 pointer-events-none z-0" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M -100 100 C 400 0, 1000 0, 1300 150 C 1600 300, 0 300, 300 450 C 800 600, 1300 400, 1700 450" fill="transparent" stroke="#d1d5db" strokeWidth="2" className="opacity-60 hidden md:block" />
      </svg>

      <div className="container mx-auto px-4 relative z-10 w-full">

        <div className="text-center mb-20 max-w-3xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-6 tracking-tight">
            Como funciona o processo de <span className="text-green-600">agendamento?</span>
          </h3>
          <p className="text-xl text-gray-500 leading-relaxed">
            Uma linha de montagem transparente e digital. Acompanhe o fluxo exato de como transformamos horas de trabalho burocrático em apenas alguns cliques.
          </p>
        </div>

        {/* Removed bounding box, grid operates directly on section bg */}
        <div className="max-w-7xl mx-auto w-full relative">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-20 md:gap-x-12 relative mb-24">
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isPast = index < activeStep;

              // Alternating heights to fall somewhat on the custom curves implicitly
              const yOffset = index % 2 === 0 ? 'md:-translate-y-4' : 'md:translate-y-8';

              return (
                <div
                  key={index}
                  className={`relative flex flex-col items-center text-center transition-all duration-700 z-10 cursor-pointer ${yOffset} ${isActive ? 'scale-110' : isPast ? 'opacity-90' : 'opacity-40 grayscale'}`}
                  onClick={() => setActiveStep(index)}
                >
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 transition-all duration-500 relative z-10
                    ${isActive ? 'bg-green-600 text-white shadow-[0_0_40px_rgba(22,163,74,0.4)]' :
                      isPast ? 'bg-white text-green-500 shadow-md border-2 border-green-100' :
                        'bg-white text-gray-400 shadow-sm border border-gray-100'}`}
                  >
                    {/* Ring aura for active */}
                    {isActive && (
                      <div className="absolute inset-0 rounded-full border border-green-400 animate-ping opacity-75"></div>
                    )}
                    {step.icon}
                  </div>

                  <div className="w-full flex flex-col items-center justify-start mt-2 px-2">
                    <h4 className={`text-lg font-bold mb-2 transition-colors duration-300 ${isActive ? 'text-green-700' : isPast ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.number}. {step.title}
                    </h4>
                    <p className={`text-sm leading-relaxed font-medium ${isActive ? 'text-gray-700' : 'text-gray-500'}`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Active Step Dynamic Detail Box */}
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-gradient-to-br from-green-50/50 via-white to-white rounded-3xl p-8 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-green-100 overflow-hidden transform transition-all duration-500">

              {/* Internal glowing blob */}
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-200 rounded-full blur-[100px] opacity-20 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">

                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 shrink-0 bg-green-600 text-white rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(22,163,74,0.3)] animate-[pulse_3s_ease-in-out_infinite]">
                    {steps[activeStep].icon}
                  </div>
                  <div>
                    <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-bold tracking-wide mb-2">
                      <span className="w-2 h-2 rounded-full bg-green-600 animate-ping"></span>
                      <span>Etapa {activeStep + 1} em foco</span>
                    </div>
                    <h5 className="text-3xl font-extrabold text-gray-900 mb-2">{steps[activeStep].title}</h5>
                    <p className="text-lg text-gray-600 leading-relaxed font-medium">{steps[activeStep].description}</p>
                  </div>
                </div>

                <div className="hidden md:flex flex-col items-end opacity-20 transition-all duration-500 transform hover:scale-105">
                  <span className="text-8xl font-black text-green-900 select-none">0{activeStep + 1}</span>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-24 relative z-20">
          <a
            href="#formularioAgendamento"
            className="group relative inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-white bg-green-600 rounded-2xl overflow-hidden shadow-[0_8px_40px_rgb(22,163,74,0.4)] hover:shadow-[0_8px_40px_rgb(22,163,74,0.6)] hover:-translate-y-1 transition-all duration-300"
          >
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
            <span className="relative flex items-center gap-3">
              Iniciar Meu Agendamento Agora
            </span>
          </a>
        </div>
      </div>
    </section>
  )
}
